// ANIMATE THIS PHOTO (2026-09-29): the website side of routes/animate.js.
// The photo is cropped to the clip's frame here, so the video is never
// stretched; the server picks the same frame from the same aspect rule.

import { resolveBackendBase } from '@/lib/resolve-backend-base';
import type { AnimateMotion } from '@/components/chopbox/ChopBox';
import { keepSiteMark } from '@/lib/site-mark';

export type AnimateFrame = 'frame_a' | 'frame_b' | 'frame_c';

export interface AnimateConfig {
  enabled: boolean;
  credits: number;
  seconds: number;
  motions: string[];
  frames: Record<AnimateFrame, { width: number; height: number }>;
}

export type AnimateStartResult =
  | { ok: true; jobId: string; credits: number }
  | { ok: false; error: string; status: number; required?: number };

export interface AnimateJobState {
  status: 'queued' | 'processing' | 'completed' | 'failed' | string;
  progress: number;
  remaining_seconds: number | null;
  result_url: string | null;
  credits_used: number;
}

function base(): string {
  try {
    return resolveBackendBase((import.meta as unknown as { env?: Record<string, unknown> }).env).replace(/\/$/, '');
  } catch {
    return '';
  }
}

function token(): string {
  try {
    return localStorage.getItem('auth_token') ?? '';
  } catch {
    return '';
  }
}

/** routes/animate.js frameFor: landscape from 1.25, portrait up to 0.8, else square. */
export function frameFor(aspect: number): AnimateFrame {
  if (!Number.isFinite(aspect) || aspect <= 0) return 'frame_c';
  if (aspect >= 1.25) return 'frame_a';
  if (aspect <= 0.8) return 'frame_b';
  return 'frame_c';
}

/** The server's offer, or null when it can't be read (the button then stays hidden). */
export async function fetchAnimateConfig(): Promise<AnimateConfig | null> {
  try {
    const res = await fetch(`${base()}/api/web/animate/config`);
    if (!res.ok) return null;
    const data = (await res.json()) as AnimateConfig;
    return data && typeof data.enabled === 'boolean' && Number.isFinite(data.credits) ? data : null;
  } catch {
    return null;
  }
}

/** The photo, center-cropped to fill the frame exactly, as a JPEG. */
export async function fitPhotoToFrame(file: Blob, config: AnimateConfig): Promise<{ blob: Blob; aspect: number }> {
  const bitmap = await createImageBitmap(file);
  try {
    const aspect = bitmap.width / Math.max(1, bitmap.height);
    const frame = config.frames[frameFor(aspect)] ?? config.frames.frame_c;
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D canvas.');
    const cover = Math.max(frame.width / bitmap.width, frame.height / bitmap.height);
    const w = bitmap.width * cover;
    const h = bitmap.height * cover;
    // JPEG has no see-through areas: a cleared background animates on white.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, frame.width, frame.height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, (frame.width - w) / 2, (frame.height - h) / 2, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) throw new Error("The photo couldn't be prepared.");
    return { blob, aspect };
  } finally {
    bitmap.close?.();
  }
}

export async function startAnimate(photo: Blob, motion: AnimateMotion, aspect: number, watermark = keepSiteMark()): Promise<AnimateStartResult> {
  const form = new FormData();
  form.append('motion', motion);
  form.append('aspect', String(aspect));
  // The site mark on the clip (lib/site-mark.ts): on unless unticked.
  form.append('watermark', watermark ? 'on' : 'off');
  form.append('photo', new File([photo], 'photo.jpg', { type: 'image/jpeg' }));
  try {
    const res = await fetch(`${base()}/api/web/animate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token()}` },
      body: form,
    });
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok && data && typeof data.job_id === 'string') {
      return { ok: true, jobId: data.job_id, credits: Number(data.credits_charged) || 0 };
    }
    return {
      ok: false,
      status: res.status,
      error: typeof data?.error === 'string' ? data.error : 'submit_failed',
      ...(Number.isFinite(Number(data?.required)) ? { required: Number(data?.required) } : {}),
    };
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }
}

export async function animateStatus(jobId: string): Promise<AnimateJobState | null> {
  try {
    const res = await fetch(`${base()}/api/web/animate/${encodeURIComponent(jobId)}`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as AnimateJobState;
  } catch {
    return null;
  }
}
